/** Tinted variant follows `currentColor`; colour variant is the full artwork from `src/public`. */
export type TappeerMarkVariant = 'tinted' | 'colour';

/** Chip surface: `light` sits on dark bands, `hero` is the pink brand disc. */
export type TappeerChipTone = 'light' | 'hero';

interface TappeerEMarkProps {
  size?: number;
  /** `colour` artwork keeps its own fills, so it needs a light surface. */
  variant?: TappeerMarkVariant;
  /** Wraps the colour artwork in a round chip so its details stay readable. */
  chip?: boolean;
  /** Chip background tone; defaults to the light surface. */
  chipTone?: TappeerChipTone;
}

/** Tappeer "e" mark — chat header icon, message avatar and menu entry. */
export function TappeerEMark({
  size = 18,
  variant = 'colour',
  chip = true,
  chipTone = 'light'
}: TappeerEMarkProps) {
  if (variant === 'tinted') {
    return (
      <svg
        width={size}
        height={size}
        viewBox='0 0 220 220'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        aria-hidden='true'
      >
        {/* main "e" body */}
        <path
          d='M110.255 184.556C89.2068 184.556 71.8841 177.664 58.2867 163.88C44.6893 149.91 37.8906 132.215 37.8906 110.794C37.8906 89.0019 44.5031 71.2135 57.7279 57.4299C71.139 43.6462 88.3686 36.7544 109.417 36.7544C131.396 36.7544 148.719 43.4599 161.385 56.8711C174.237 70.2822 180.663 88.3499 180.663 111.074V121.97H158.032C158.032 121.97 160.272 115.456 153.028 109.37C148.507 105.572 141.245 100.285 131.489 100.285C121.733 100.285 115.568 107.062 105.364 111.406C95.1627 115.751 79.203 111.406 79.203 111.406C81.6244 129.473 92.2802 149.631 111.093 149.631C119.289 149.631 126.087 148.234 131.489 145.44C136.891 142.646 140.337 138.734 141.827 133.705H181.222C178.614 149.165 170.884 161.552 158.032 170.865C145.366 179.992 129.44 184.556 110.255 184.556Z'
          fill='currentColor'
        />
        {/* tapir snout/ear detail */}
        <path
          d='M88.2189 49.2438L80.3457 58.4342C80.3457 58.4342 66.5257 54.5405 65.2332 49.7615C63.9384 44.9822 65.7511 39.7999 68.6882 37.5627C71.623 35.3254 74.3489 35.0924 77.972 35.7995C81.5928 36.5069 88.2189 49.2438 88.2189 49.2438Z'
          fill='currentColor'
          opacity='0.55'
        />
        {/* eye highlight */}
        <path
          d='M135.404 64.2582C135.492 58.6134 130.988 53.9659 125.343 53.8775C119.698 53.7891 115.051 58.2936 114.962 63.9381C114.874 69.5829 119.378 74.2304 125.023 74.3188C130.668 74.4071 135.315 69.9029 135.404 64.2582Z'
          fill='currentColor'
          opacity='0.15'
        />
        <path
          d='M134.251 65.376C134.319 60.9858 130.817 57.3709 126.426 57.3023C122.035 57.2334 118.421 60.7368 118.353 65.1272C118.282 69.5174 121.787 73.1323 126.176 73.2009C130.567 73.2698 134.251 69.7664 134.251 65.376Z'
          fill='currentColor'
          opacity='0.8'
        />
      </svg>
    );
  }

  const image = (
    <img
      src='/tappeer-e.svg'
      alt=''
      aria-hidden='true'
      width={size}
      height={size}
      className='block'
    />
  );

  if (!chip) return image;

  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-full shadow-sm ${
        chipTone === 'hero' ? 'bg-secondary-base' : 'bg-background-input'
      }`}
      style={{ width: size + 8, height: size + 8 }}
    >
      {image}
    </span>
  );
}

/** Tappeer horizontal wordmark — black artwork, needs a light background. */
export function TappeerWordmark({ width = 168 }: { width?: number }) {
  return (
    <img src='/tappeer-horizontal.svg' alt='Tappeer' className='block h-auto' style={{ width }} />
  );
}
